import ReactGA from 'react-ga4';
import { fetchStreamedAIResponse } from 'services/ai-assistant-api';
import serviceApi from 'services/api';
import auth from 'services/auth';
import { AIAssistantMessageDirection, AIAssistantMessagePosition } from 'utils/constants';

const api = process.env.REACT_APP_ENVIRONMENT === 'local' ? auth : serviceApi;

export interface AIAssistantMessageType {
  sender: string;
  message: string;
  direction: AIAssistantMessageDirection;
  position?: AIAssistantMessagePosition;
  timestamp?: string;
}

export interface AIAssistantRequest {
  session_id?: string;
  roleUuid: string;
  prompt: string;
}

export interface RawMessage {
  author: 'user' | 'assistant';
  message: string;
  timestamp: string;
}

export type ParsedConversation = RawMessage[];

export type ChatBotSessionProps = {
  session_id: string;
  role_uuid: string;
  conversation: string;
  updated_at: string;
};

/**
 * The SendFeedbackRequest type represents a request to send feedback.
 */
export type SendFeedbackRequest = {
  is_good: boolean;
  session_id: string;
  chat?: string;
  feedback?: string;
};

/**
 * The SendFeedbackResponse type represents a response from SendFeedback.
 */
export type SendFeedbackResponse = {
  uuid: string;
  is_good: boolean;
  session_id: string;
  chat: string;
  feedback?: string;
};

/**
 * The GetAIAnalyticsParams type represents the query parameters for getFeedbacks and getConversations.
 */
export type GetAIAnalyticsParams = {
  excludeBline: boolean;
  start?: string;
  end?: string;
};

/**
 * The FeedbackResponse type represents the feedback data returned by the server.
 */
export interface GetFeedbackResponse {
  uuid: string;
  timestamp: string;
  username: string;
  platform: string;
  prompt: string;
  answer: string;
  feedback: string;
  isGood: boolean;
}

/**
 * The ConversationResponse type represents the conversation data returned by the server.
 */
export interface GetConversationResponse {
  uuid: string;
  username: string;
  platform: string;
  conversation: string;
  updated_at: string;
}

const fetchAIResponse = async (
  payload: AIAssistantRequest,
  handleStreamedMessage: (message: string) => void
): Promise<void> => {
  ReactGA.event({
    category: 'User',
    action: 'AI Assistant Message Sent',
    label: `Prompt: ${payload.prompt}, Role Uuid: ${payload.roleUuid}, Session Uuid: ${payload.session_id}`
  });
  try {
    await fetchStreamedAIResponse(payload, handleStreamedMessage);
  } catch (error) {
    console.error('Error fetching AI response:', error);
    handleStreamedMessage(
      'Oops! Our AI tripped over a wire 🤖⚡. Our deepest apologies. Please ask again.'
    );
  }
};

/* The function `GetChatBotSession` is an asynchronous function that makes a GET request to the `/apiv1/admin/chatbot/session` endpoint and returns
all the session data with conversation in the response. */
const getChatBotSession = async () => {
  const response = await serviceApi.get<ChatBotSessionProps>('/apiv1/chatbot_session');
  return response?.data;
};

/* The function `SendFeedback` is an asynchronous function that makes a POST request to the `/apiv1/admin/chatbot/feedback` endpoint with the payload and returns
the feedback response. */
const sendFeedback = async (payload: SendFeedbackRequest, feedbackUuid?: string) => {
  ReactGA.event({
    category: 'User',
    action: 'AI Assistant Feedback',
    label: `Feedback Uuid: ${feedbackUuid}, Feedback: ${payload.feedback}, Session Uuid: ${payload.session_id}, Is Good: ${payload.is_good}`
  });
  let url = '/apiv1/admin/chatbot/feedback';
  if (feedbackUuid) {
    url += `?feedback_uuid=${feedbackUuid}`;
  }

  const response = await api.post<SendFeedbackResponse>(url, payload);
  return response?.data;
};

const UpdateUserContext = async (userSessionToken: string) => {
  const url = '/apiv1/admin/chatbot/user_context';
  const response = await api.post<string>(url, { role_uuid: userSessionToken });
  return response?.data;
};

/* The function `getFeedbacks` is an asynchronous function that makes a GET request to the `/apiv1/chatbot/feedback` endpoint and returns
all the feedbacks based on the query parameters. */
const getFeedbacks = async (params: GetAIAnalyticsParams): Promise<GetFeedbackResponse[]> => {
  const { excludeBline, start, end } = params;

  const response = await serviceApi.get<GetFeedbackResponse[]>('/apiv1/chatbot/feedback', {
    params: {
      exclude_bline: excludeBline,
      start,
      end
    }
  });
  return response?.data;
};

/* The function `getConversations` is an asynchronous function that makes a GET request to the `/apiv1/chatbot/conversations` endpoint and returns
all the conversations based on the query parameters. */
const getConversations = async (
  params: GetAIAnalyticsParams
): Promise<GetConversationResponse[]> => {
  const { excludeBline, start, end } = params;

  const response = await serviceApi.get<GetConversationResponse[]>('/apiv1/chatbot/conversations', {
    params: {
      exclude_bline: excludeBline,
      start,
      end
    }
  });
  return response?.data;
};

const AIAssistantService = {
  fetchAIResponse,
  UpdateUserContext,
  getChatBotSession,
  sendFeedback,
  getFeedbacks,
  getConversations
};

export default AIAssistantService;
